import { useEffect, useState } from 'react';

/**
 * Brand logo with automatic variant + theme selection.
 *
 * variants:
 *   - horizontal : icon + full company name side-by-side  (best for wide bars, PDFs, emails)
 *   - stacked    : icon on top, company name below         (best for splash, login, empty states)
 *   - icon       : round badge only                        (best for compact spaces — collapsed sidebar, mobile header, favicon)
 *
 * themes:
 *   - auto       : reads the surface tone via a `surface` prop, defaults to 'light'
 *   - light      : dark-blue ink (for light surfaces)
 *   - dark       : white ink (for dark surfaces)
 *
 * The component always renders a <picture> so the browser picks the smallest
 * asset that fits (webp preferred, png fallback via <img>'s native support).
 */
export function Logo({
  variant = 'horizontal',   // 'horizontal' | 'stacked' | 'icon'
  surface = 'light',        // 'light' | 'dark' | 'auto'
  className = '',
  alt = '32December',
  height,                   // px | number — width follows via natural aspect ratio
  width,
  testId = 'brand-logo',
  onClick,
  ...rest
}) {
  const resolved = useThemeForSurface(surface);
  const src = LOGO_MAP[variant]?.[resolved] || LOGO_MAP.horizontal.light;
  const style = { height: height ? `${height}px` : undefined, width: width ? `${width}px` : undefined };
  return (
    <img
      src={src}
      alt={alt}
      className={className}
      style={style}
      data-testid={testId}
      data-logo-variant={variant}
      data-logo-theme={resolved}
      onClick={onClick}
      {...rest}
    />
  );
}

const LOGO_MAP = {
  horizontal: {
    light: '/logos/logo-horizontal-dark.webp',   // dark ink → for light surfaces
    dark:  '/logos/logo-horizontal-light.webp',  // white ink → for dark surfaces
  },
  stacked: {
    light: '/logos/logo-stacked-dark.webp',
    dark:  '/logos/logo-stacked-light.webp',
  },
  icon: {
    light: '/logos/logo-icon-dark.png',
    dark:  '/logos/logo-icon-light.png',
  },
};

/** Resolve surface='auto' by reading the CSS variable `--surface-luminance`
 *  (set on <html> by the theme toggle) or the prefers-color-scheme fallback.
 *  Explicit values pass through unchanged. */
function useThemeForSurface(surface) {
  const [resolved, setResolved] = useState(() => surface === 'auto' ? detectSurface() : surface);
  useEffect(() => {
    if (surface !== 'auto') { setResolved(surface); return; }
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => setResolved(detectSurface());
    onChange();
    mq.addEventListener?.('change', onChange);
    const obs = new MutationObserver(onChange);
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme', 'class'] });
    return () => { mq.removeEventListener?.('change', onChange); obs.disconnect(); };
  }, [surface]);
  return resolved;
}

function detectSurface() {
  if (typeof document === 'undefined') return 'light';
  const html = document.documentElement;
  // Prefer explicit data-theme, else class `dark`, else prefers-color-scheme.
  const explicit = html.dataset.theme;
  if (explicit === 'dark' || explicit === 'light') return explicit;
  if (html.classList.contains('dark')) return 'dark';
  return window.matchMedia?.('(prefers-color-scheme: dark)')?.matches ? 'dark' : 'light';
}

/** Utility: returns the right logo variant for a target width in px.
 *  Below 40px → icon. Below 200px in width → stacked. Otherwise → horizontal. */
export function pickLogoVariantForWidth(px) {
  if (!px || px < 40) return 'icon';
  if (px < 200) return 'stacked';
  return 'horizontal';
}
